import { ref } from 'vue'
import store from '@/store'

const CACHE_TTL_MS = 5 * 60 * 1000
// Las URLs firmadas de S3 expiran (1 min en UserProfileResource). El cache no
// debe servir una URL vencida: si la respuesta trae firma, la frescura la manda
// la expiración de la URL, no el TTL general. Margen de seguridad anti-skew.
const SIGNED_URL_SKEW_MS = 5 * 1000

let cached = null
let inflight = null

// Versión de la caché: se incrementa en cada invalidación (logout, cambio de
// rol, guardado de perfil) para que los componentes montados (p. ej. el avatar
// del header, que no se remonta al navegar) refresquen sus datos en caliente.
export const profileCacheVersion = ref(0)

export const parseSignedUrlExpiry = (signedUrl) => {  if (typeof signedUrl !== 'string' || !signedUrl.includes('X-Amz-')) {
    return null
  }
  try {
    const url = new URL(signedUrl)
    const expiresIn = Number(url.searchParams.get('X-Amz-Expires'))
    const amzDate = url.searchParams.get('X-Amz-Date')
    if (!expiresIn || !amzDate) {
      return null
    }
    // X-Amz-Date viene en formato básico ISO 8601 (20260810T123456Z).
    const time = `${amzDate.slice(9, 11)}:${amzDate.slice(11, 13)}:${amzDate.slice(13, 15)}Z`
    const iso = `${amzDate.slice(0, 4)}-${amzDate.slice(4, 6)}-${amzDate.slice(6, 8)}T${time}`
    const issuedAt = Date.parse(iso)
    if (Number.isNaN(issuedAt)) {
      return null
    }
    return issuedAt + expiresIn * 1000
  } catch {
    return null
  }
}

const isFresh = () => {
  if (cached === null) {
    return false
  }

  const expiry = parseSignedUrlExpiry(cached?.data?.user_profile?.image_profile)
  if (expiry !== null) {
    return Date.now() < expiry - SIGNED_URL_SKEW_MS
  }

  return Date.now() - cached.at < CACHE_TTL_MS
}

export const fetchProfileOnce = async () => {
  if (!isFresh() && inflight === null) {
    const pending = store.dispatch('userProfile/findUserByToken').then(
      (result) => {
        // Si la caché fue limpiada mientras la petición estaba en vuelo (logout
        // en otra pestaña o clearProfileCache durante la sesión), la respuesta
        // pertenece a la sesión saliente: no debe repoblar la caché.
        if (inflight === pending) {
          cached = { ...result, at: Date.now() }
          inflight = null
          return cached
        }
        // Resolver null rompería a los consumidores (destructuring sin guarda
        // en UserProfileMenu): se resuelve un objeto de error descartable.
        return { error: true, data: null, message: 'profile-fetch-invalidated' }
      },
      () => {
        if (inflight === pending) {
          inflight = null
        }
        return { error: true, data: null, message: 'profile-fetch-failed' }
      }
    )
    inflight = pending
  }

  return inflight || cached
}

export const clearProfileCache = () => {
  cached = null
  inflight = null
  profileCacheVersion.value += 1
}
